import { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';

const API = 'https://affinityconcept.tech/api';

const CATEGORIES = [
  'Utilities', 'Digital Subscriptions', 'Insurance',
  'Education', 'Housing', 'Transport', 'Tech & Hosting',
  'Memberships', 'Government', 'Finance', 'Home Staff', 'Other'
];

const FREQUENCIES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'weekly', label: 'Weekly' },
];

const ICONS = {
  'Utilities': '💡',
  'Digital Subscriptions': '📱',
  'Insurance': '🛡',
  'Education': '🏫',
  'Housing': '🏠',
  'Tech & Hosting': '💻',
  'Memberships': '🎫',
  'Government': '🏛',
'Transport': '🚗',  
'Finance': '💳',
'Home Staff': '👩‍👧',
  'Other': '📦',
};

const FORM_DEFAULT = {
  name: '', category: 'Utilities', amount: '', currency: 'AED',
  frequency: 'monthly', next_due_date: '', reminder_days: 14,
  payment_method: '', notes: '', is_active: true
};

function daysUntil(dateStr) {
  const today = new Date();
  const due = new Date(dateStr);
  const diff = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
  return diff;
}

function urgencyColor(days) {
  if (days < 0) return 'text-red-600 bg-red-100';
  if (days <= 7) return 'text-red-500 bg-red-50';
  if (days <= 14) return 'text-orange-500 bg-orange-50';
  if (days <= 30) return 'text-yellow-500 bg-yellow-50';
  return 'text-green-500 bg-green-50';
}

function dueLabel(days) {
  if (days < 0) return `Overdue ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'}`;
  if (days === 0) return 'Due today!';
  if (days === 1) return 'Tomorrow';
  return `${days} days`;
}

function frequencyLabel(freq) {
  const map = { monthly: 'Monthly', quarterly: 'Quarterly', yearly: 'Yearly', weekly: 'Weekly' };
  return map[freq] || freq;
}

function yearlyTotal(amount, frequency) {
  const map = { monthly: 12, quarterly: 4, yearly: 1, weekly: 52 };
  return amount * (map[frequency] || 1);
}

export default function RecurringBills({ token, onLogout }) {
  const [bills, setBills] = useState([]);
  const [user, setUser] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filterCategory, setFilterCategory] = useState('All');
  const [form, setForm] = useState(FORM_DEFAULT);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/recurring-bills`, { headers }).then(r => setBills(r.data));
    axios.get(`${API}/me`, { headers }).then(r => setUser(r.data));
  }, []);

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(FORM_DEFAULT);
  };

  const startAdd = () => {
    setEditingId(null);
    setForm(FORM_DEFAULT);
    setShowForm(true);
  };

  const startEdit = (b) => {
    setEditingId(b.id);
    setForm({
      name: b.name || '',
      category: b.category || 'Utilities',
      amount: b.amount ?? '',
      currency: b.currency || 'AED',
      frequency: b.frequency || 'monthly',
      next_due_date: b.next_due_date ? b.next_due_date.slice(0, 10) : '',
      reminder_days: b.reminder_days ?? 14,
      payment_method: b.payment_method || '',
      notes: b.notes || '',
      is_active: b.is_active ?? true,
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      amount: parseFloat(form.amount),
      reminder_days: parseInt(form.reminder_days),
    };
    if (editingId) {
      await axios.put(`${API}/recurring-bills/${editingId}`, payload, { headers });
    } else {
      await axios.post(`${API}/recurring-bills`, payload, { headers });
    }
    const res = await axios.get(`${API}/recurring-bills`, { headers });
    setBills(res.data);
    closeForm();
  };

  const handleDelete = async (id) => {
    await axios.delete(`${API}/recurring-bills/${id}`, { headers });
    setBills(prev => prev.filter(b => b.id !== id));
  };

  const filtered = filterCategory === 'All' ? bills : bills.filter(b => b.category === filterCategory);

  const totalMonthly = bills.reduce((sum, b) => {
    const map = { monthly: 1, quarterly: 1/3, yearly: 1/12, weekly: 4.33 };
    return sum + (b.amount * (map[b.frequency] || 1));
  }, 0);

  const totalYearly = bills.reduce((sum, b) => sum + yearlyTotal(b.amount, b.frequency), 0);

  const upcoming = [...bills]
    .filter(b => daysUntil(b.next_due_date) <= 30)
    .sort((a, b) => daysUntil(a.next_due_date) - daysUntil(b.next_due_date));

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <Sidebar onLogout={onLogout} user={user} />
      <main className="flex-1 overflow-y-auto p-4 md:p-8 pt-16 md:pt-8">

        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Recurring Bills</h1>
            <p className="text-gray-500 text-sm">
              Monthly: <span className="font-semibold text-red-500">AED {totalMonthly.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
              <span className="mx-2">•</span>
              Yearly: <span className="font-semibold text-red-500">AED {totalYearly.toLocaleString(undefined, {maximumFractionDigits: 0})}</span>
            </p>
          </div>
          <button onClick={() => (showForm ? closeForm() : startAdd())}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-xl text-sm font-medium transition">
            {showForm ? 'Close' : '+ Add Bill'}
          </button>
        </div>

        {/* Upcoming payments */}
        {upcoming.length > 0 && (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-6">
            <h2 className="text-sm font-semibold text-gray-900 mb-3">🔔 Due in next 30 days</h2>
            <div className="space-y-2">
              {upcoming.map(b => {
                const days = daysUntil(b.next_due_date);
                return (
                  <div key={b.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                    <div className="flex items-center gap-3">
                      <span className="text-lg">{ICONS[b.category] || '📦'}</span>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{b.name}</p>
                        <p className="text-xs text-gray-400">{new Date(b.next_due_date).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${urgencyColor(days)}`}>
                        {dueLabel(days)}
                      </span>
                      <span className="text-sm font-semibold text-gray-900">
                        {b.currency} {b.amount.toLocaleString()}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <p className="text-xs text-gray-500 mb-1">Monthly total</p>
            <p className="text-xl font-bold text-red-500">AED {totalMonthly.toLocaleString(undefined, {maximumFractionDigits: 0})}</p>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <p className="text-xs text-gray-500 mb-1">Yearly total</p>
            <p className="text-xl font-bold text-red-500">AED {totalYearly.toLocaleString(undefined, {maximumFractionDigits: 0})}</p>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <p className="text-xs text-gray-500 mb-1">Active bills</p>
            <p className="text-xl font-bold text-gray-900">{bills.length}</p>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <p className="text-xs text-gray-500 mb-1">Due this month</p>
            <p className="text-xl font-bold text-orange-500">{upcoming.length}</p>
          </div>
        </div>

        {/* Category filter */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-4 scrollbar-hide">
          <button onClick={() => setFilterCategory('All')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition ${
              filterCategory === 'All' ? 'bg-gray-900 text-white' : 'bg-white text-gray-500 border border-gray-200'
            }`}>
            All
          </button>
          {CATEGORIES.map(cat => (
            <button key={cat} onClick={() => setFilterCategory(cat)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition ${
                filterCategory === cat ? 'bg-gray-900 text-white' : 'bg-white text-gray-500 border border-gray-200'
              }`}>
              {ICONS[cat]} {cat}
            </button>
          ))}
        </div>

        {/* Add form */}
        {showForm && (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-6">
            <h2 className="text-base font-semibold mb-4">{editingId ? 'Edit Bill' : 'New Recurring Bill'}</h2>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input required placeholder="Name (e.g. Netflix, DEWA, School fees)"
                value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
              <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}
                className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
              <div className="flex gap-2">
                <input required type="number" step="any" placeholder="Amount"
                  value={form.amount} onChange={e => setForm({...form, amount: e.target.value})}
                  className="border border-gray-200 rounded-xl px-3 py-2 text-sm flex-1 focus:outline-none focus:ring-2 focus:ring-green-500" />
                <select value={form.currency} onChange={e => setForm({...form, currency: e.target.value})}
                  className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none">
                  <option>AED</option>
                  <option>USD</option>
                </select>
              </div>
              <select value={form.frequency} onChange={e => setForm({...form, frequency: e.target.value})}
                className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                {FREQUENCIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Next due date</label>
                <input required type="date" value={form.next_due_date}
                  onChange={e => setForm({...form, next_due_date: e.target.value})}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Remind me X days before</label>
                <input type="number" value={form.reminder_days}
                  onChange={e => setForm({...form, reminder_days: e.target.value})}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
              <input placeholder="Payment method (e.g. Credit card, Bank transfer)"
                value={form.payment_method} onChange={e => setForm({...form, payment_method: e.target.value})}
                className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
              <input placeholder="Notes (optional)"
                value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}
                className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
              <label className="md:col-span-2 flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input type="checkbox" checked={form.is_active}
                  onChange={e => setForm({...form, is_active: e.target.checked})}
                  className="w-4 h-4 rounded accent-green-600" />
                Active (counts toward totals &amp; reminders)
              </label>
              <div className="md:col-span-2 flex gap-3">
                <button type="submit" className="bg-green-600 text-white px-5 py-2 rounded-xl text-sm font-medium">
                  {editingId ? 'Update' : 'Save'}
                </button>
                <button type="button" onClick={closeForm}
                  className="border border-gray-200 px-5 py-2 rounded-xl text-sm">Cancel</button>
              </div>
            </form>
          </div>
        )}

        {/* Bills list */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Category</th>
                  <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Frequency</th>
                  <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Next Due</th>
                  <th className="text-right p-4 text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="text-right p-4 text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Yearly</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-12 text-gray-400 text-sm">
                    No recurring bills yet. Add your first one!
                  </td></tr>
                )}
                {filtered.map(b => {
                  const days = daysUntil(b.next_due_date);
                  return (
                    <tr key={b.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{ICONS[b.category] || '📦'}</span>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{b.name}</p>
                            <p className="text-xs text-gray-400 md:hidden">
                              {b.category} • {frequencyLabel(b.frequency)} • Due: {new Date(b.next_due_date).toLocaleDateString()}
                            </p>
                            {b.notes && <p className="text-xs text-gray-400 hidden md:block">{b.notes}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="p-4 hidden md:table-cell">
                        <span className="bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded-full">{b.category}</span>
                      </td>
                      <td className="p-4 hidden md:table-cell">
                        <span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full">{frequencyLabel(b.frequency)}</span>
                      </td>
                      <td className="p-4 hidden md:table-cell">
                        <div>
                          <p className="text-sm text-gray-700">{new Date(b.next_due_date).toLocaleDateString()}</p>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${urgencyColor(days)}`}>
                            {dueLabel(days)}
                          </span>
                        </div>
                      </td>
                      <td className="p-4 text-right">
                        <p className="text-sm font-semibold text-gray-900">{b.currency} {b.amount.toLocaleString()}</p>
                        {b.payment_method && <p className="text-xs text-gray-400">{b.payment_method}</p>}
                      </td>
                      <td className="p-4 text-right hidden md:table-cell">
                        <p className="text-xs text-gray-500">AED {yearlyTotal(b.amount, b.frequency).toLocaleString()}/yr</p>
                      </td>
                      <td className="p-4 text-right whitespace-nowrap">
                        <button onClick={() => startEdit(b)}
                          className="text-gray-300 hover:text-blue-500 text-sm transition mr-3" title="Edit">✏️</button>
                        <button onClick={() => handleDelete(b.id)}
                          className="text-gray-300 hover:text-red-500 text-sm transition" title="Delete">✕</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
